import "@/styles/identityCheck.scss";

import { Card } from "@amzn/stencil-react-components/card";
import {
  Chip,
  ChipBackgroundColor,
  ChipSize,
} from "@amzn/stencil-react-components/chip";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Radio } from "@amzn/stencil-react-components/radio";
import { Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFillSmall from "@amzn/stencil-react-icons/icons/icon-check-circle-fill-small";
import IconClockSmall from "@amzn/stencil-react-icons/icons/icon-clock-small";
import IconPaymentSmall from "@amzn/stencil-react-icons/icons/icon-payment-small";
import React, { CSSProperties, ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { WizardPathType } from "@/stores/constants";

/**
 * The type of cards that can appear in the WizardPathChooser component which is
 * the step in which new hires can decide how their ID verification will proceed
 * (e.g. manual, automated, contacting support to verify via Chime Call, etc.)
 */
export type Card = WizardPathType | "ContactSupport";

export type CardClickedCallbackEventBody = {
  cardType: Card;
  checked?: boolean;
};
export type EntitlementCardProps = {
  /** Is this card selected? */
  selected?: boolean;
  /** Controls if this card is selected or not initially. Controls radio button state and visual changes applied on select. */
  defaultSelected?: boolean;
  /** Execute function when the card is selected. The change can come from either a direct click on the radio button or from clicking anywhere on the card.*/
  cardClickedCallback?: (evt: CardClickedCallbackEventBody) => void;
  /** Is this the "Recommended" approach? Controls if the card shows a "✓ Recommended" badge. */
  recommended?: boolean;
  /** Title i18n ID to use. */
  titlei18nId?: string;
  /** Body i18n ID to use. */
  bodyi18nId?: string;
  /** The picture or symbol that represents this card. */
  bodyIcon?: ReactNode;
  /** The type of card. Used to tell this component about itself for metrics, debug, etc. */
  cardType: WizardPathType | "ContactSupport";
  /** What i18n to use to describe the verification type. For example the ID for the word "Automated". */
  verificationTypei18n?: string;
  /** The icon to the left of the *Verification Type* label */
  verificationTypeIcon?: ReactNode;
  /**
   * Time to complete this step in minutes. Used for telling the user how long it's estimated for this method of verification to take.
   * If it is an ordered pair `[number, number]`, the estimated time to complete will show as a range like `15-60` minutes
   */
  estimatedTimeToCompletionMinutes?: number | [number, number];
  dataTestId?: string;
  /** If the card should be selectable or not. Is default false. */
  disabled?: boolean;
  /** If the card should show only the auto IVV selection or not. Is default false. */
  isOnlyAutoIvvEligible?: boolean;
  /** What i18n to use to describe which IDs are required for the given verification type and country */
  idsRequiredi18n?: string;
} & Pick<CSSProperties, "width" | "height" | "color">;

export const IdentityVerificationSelectionCard = (
  props: EntitlementCardProps
) => {
  const intl = useIntl();

  const handleClick = () => {
    if (!props.disabled && !props.isOnlyAutoIvvEligible) {
      void props.cardClickedCallback?.({
        cardType: props.cardType,
      });
    }
  };

  const unitFromEstimatedTimeToCompletion = () => {
    if (props.estimatedTimeToCompletionMinutes) {
      if (props.estimatedTimeToCompletionMinutes === 1) {
        // If is one minute, return "minute"
        return intl.formatMessage({
          id: "identityCheck-pathchooser-card-unitMinute",
        });
      } else {
        // If is 0 or 2-60, return "minutes"
        return intl.formatMessage({
          id: "identityCheck-pathchooser-card-unitMinutes",
        });
      }
    } else {
      return "";
    }
  };

  const selectionCardContainerInnerContents = (
    <>
      <Row alignItems="center" justifyContent="space-between">
        {props.titlei18nId && (
          <Text
            fontWeight="bold"
            fontSize={props.isOnlyAutoIvvEligible ? "T300" : "T200"}
            color="neutral90"
            dataTestId="IDVSCTitle"
          >
            <FormattedMessage id={props.titlei18nId} />
          </Text>
        )}
        {!props.isOnlyAutoIvvEligible && props.recommended && (
          <Chip
            dataTestComponent="ChipRecommended"
            backgroundColor={ChipBackgroundColor.Green}
            dataTestId="ChipRecommended"
            size={ChipSize.ExtraSmall}
          >
            <Row alignItems="center">
              <IconCheckCircleFillSmall />
              <Spacer width="S200" />
              <Text fontSize="T100" fontWeight="medium" color="neutral90">
                <FormattedMessage id="identityCheck-pathchooser-card-recommended" />
              </Text>
            </Row>
          </Chip>
        )}
        {!props.isOnlyAutoIvvEligible && (
          <Radio
            onChange={() => {
              handleClick();
            }}
            name={`${String(props.cardType)}-radio`}
            checked={props.selected}
            dataTestId="IDVSCRadio"
            disabled={props.disabled}
          />
        )}
      </Row>
      <Row height="S300" />
      {props.bodyIcon && <Row dataTestId="IDVSCIcon">{props.bodyIcon}</Row>}
      {props.bodyi18nId && (
        <>
          <Spacer height="S200" />
          <Row>
            <Text
              fontSize={props.isOnlyAutoIvvEligible ? "T200" : "T100"}
              color="neutral90"
              lineHeight="22px"
              dataTestId="IDVSCBody"
            >
              <FormattedMessage id={props.bodyi18nId} />
            </Text>
          </Row>
        </>
      )}
      {props.estimatedTimeToCompletionMinutes && (
        <>
          <Spacer height="S300" />
          <Row alignItems="center">
            <IconClockSmall
              aria-hidden={true}
              color={
                props.recommended || props.isOnlyAutoIvvEligible
                  ? "primary70"
                  : "neutral100"
              }
            />
            <Spacer width="S200" />
            <Text
              fontSize={props.isOnlyAutoIvvEligible ? "T200" : "T100"}
              color="neutral90"
              dataTestId="IDVSCEstTimeCompletion"
            >
              <FormattedMessage
                id={
                  props.cardType === "IVV"
                    ? "identityCheck-pathchooser-card-estCompletionAutomated"
                    : "identityCheck-pathchooser-card-estCompletionManual"
                }
                values={{
                  b: (chunks: unknown) => <b>{chunks}</b>,
                  count: (
                    <>
                      {Array.isArray(props.estimatedTimeToCompletionMinutes)
                        ? `${props.estimatedTimeToCompletionMinutes[0]}-${props.estimatedTimeToCompletionMinutes[1]}`
                        : props.estimatedTimeToCompletionMinutes}
                    </>
                  ),
                  unit: <>{unitFromEstimatedTimeToCompletion()}</>,
                }}
              />
            </Text>
          </Row>
        </>
      )}
      {props.verificationTypei18n && (
        <>
          <Spacer height="S200" />
          <Row alignItems="center">
            {props.verificationTypeIcon}
            <Spacer width="S200" />
            <Text
              fontSize={props.isOnlyAutoIvvEligible ? "T200" : "T100"}
              color="neutral90"
              dataTestId="IDVSCVType"
            >
              <FormattedMessage
                id={props.verificationTypei18n}
                values={{ b: (chunks: unknown) => <b>{chunks}</b> }}
              />
            </Text>
          </Row>
        </>
      )}
      {props.idsRequiredi18n && (
        <>
          <Spacer height="S200" />
          <Row alignItems="center">
            <IconPaymentSmall
              aria-hidden={true}
              color={
                props.recommended || props.isOnlyAutoIvvEligible
                  ? "primary70"
                  : "neutral100"
              }
            />
            <Spacer width="S200" />
            <Text
              fontSize={props.isOnlyAutoIvvEligible ? "T200" : "T100"}
              color="neutral90"
              dataTestId="IDVSCVID"
            >
              <FormattedMessage
                id={props.idsRequiredi18n}
                values={{ b: (chunks: unknown) => <b>{chunks}</b> }}
              />
            </Text>
          </Row>
        </>
      )}
    </>
  );
  const selectionCardState = props.selected ? "active" : "";

  return (
    <Card
      className={`idv-selection-card ${selectionCardState}`}
      data-test-id={props.dataTestId}
      onClick={handleClick}
    >
      <Col>{selectionCardContainerInnerContents}</Col>
    </Card>
  );
};
